import { useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { apiJson } from "@/lib/api";
import { PageHeader } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeft, FileText, Calendar, DollarSign } from "lucide-react";
import { useLocation } from "wouter";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { ParentContextBar, type ParentContextData } from "@/components/hierarchy/ParentContextBar";
import { useMemo } from "react";

export default function BookingDetailPage() {
    const [, params] = useRoute("/admin/bookings/:id");
    const [, setLocation] = useLocation();
    const id = params?.id ? parseInt(params.id) : 0;

    // Fetch booking details
    const { data: booking, isLoading } = useQuery<{
        id: number;
        name: string;
        status?: string;
        bookingValue?: number | null;
        cycleId?: number | null;
        cycleName?: string | null;
        advertiserId?: number | null;
        advertiserName?: string | null;
        clientId?: number | null;
        clientName?: string | null;
        retailerId?: number | null;
        retailerName?: string | null;
        retailerEventId?: number | null;
        notes?: string | null;
        createdAt?: string;
    }>({
        queryKey: ["booking", id],
        queryFn: () => apiJson(`/api/bookings/${id}`),
        enabled: !!id,
    });

    // Fetch campaigns for this booking
    const { data: campaignsData, isLoading: campaignsLoading } = useQuery<{ data: any[] }>({
        queryKey: ["campaigns", "booking", id],
        queryFn: () => apiJson(`/api/campaigns?bookingId=${id}`),
        enabled: !!id,
    });
    const campaigns = campaignsData?.data || [];

    // Parent context for hierarchy bar
    const parentContextData = useMemo((): ParentContextData => ({
        clientId: booking?.clientId,
        clientName: booking?.clientName,
        advertiserId: booking?.advertiserId,
        advertiserName: booking?.advertiserName,
        bookingId: booking?.id,
        bookingName: booking?.name,
        campaignName: "", // No campaign - we're at booking level
    }), [booking]);

    if (isLoading) {
        return (
            <div className="flex h-[50vh] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    if (!booking) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <h2 className="text-xl font-semibold">Booking not found</h2>
                    <Button variant="link" onClick={() => setLocation("/admin/bookings")}>
                        Back to Booking List
                    </Button>
                </div>
            </div>
        );
    }

    const formatDate = (dateString?: string) => {
        if (!dateString) return "—";
        return new Date(dateString).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    return (
        <div className="p-6 space-y-6">
            {/* Parent Context Bar - shows Client → Advertiser → Booking */}
            <ParentContextBar data={parentContextData} className="-mx-6 -mt-6 mb-4" />

            <Button
                variant="ghost"
                className="mb-2 pl-0 hover:pl-2 transition-all"
                onClick={() => setLocation("/admin/bookings")}
            >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Bookings
            </Button>

            <PageHeader
                title={booking.name || `Booking #${booking.id}`}
                description={
                    <div className="flex items-center gap-2 mt-1">
                        <Badge variant={booking.status === "confirmed" ? "default" : "secondary"}>
                            {booking.status || "Draft"}
                        </Badge>
                        {booking.advertiserName && (
                            <>
                                <span className="text-muted-foreground">•</span>
                                <a
                                    href={`/admin/advertisers/${booking.advertiserId}`}
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                    {booking.advertiserName}
                                </a>
                            </>
                        )}
                        {booking.clientName && (
                            <>
                                <span className="text-muted-foreground">→</span>
                                <a
                                    href={`/admin/clients/${booking.clientId}`}
                                    className="text-blue-600 hover:text-blue-800 hover:underline"
                                >
                                    {booking.clientName}
                                </a>
                            </>
                        )}
                    </div>
                }
            />

            <Tabs defaultValue="overview" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="campaigns">
                        Campaigns
                        <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] px-1">
                            {campaigns.length}
                        </Badge>
                    </TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Booking Value</CardTitle>
                                <DollarSign className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">
                                    {booking.bookingValue
                                        ? `€${Number(booking.bookingValue).toLocaleString()}`
                                        : "—"
                                    }
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Cycle</CardTitle>
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">
                                    {booking.cycleName || (booking.cycleId ? `Cycle ${booking.cycleId}` : "—")}
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Created</CardTitle>
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">
                                    {formatDate(booking.createdAt)}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {booking.notes && (
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm font-medium">Notes</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="text-sm whitespace-pre-wrap">{booking.notes}</div>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>

                {/* Campaigns Tab */}
                <TabsContent value="campaigns">
                    <Card>
                        <CardHeader>
                            <CardTitle>Booking Campaigns</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {campaignsLoading ? (
                                <div className="flex items-center justify-center p-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                            ) : campaigns.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Campaign Name</TableHead>
                                            <TableHead>Cycle</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Retailer</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {campaigns.map((campaign: any) => (
                                            <TableRow key={campaign.id}>
                                                <TableCell className="font-medium">
                                                    <a
                                                        href={`/campaigns/${campaign.id}`}
                                                        className="text-blue-600 hover:text-blue-800 hover:underline"
                                                    >
                                                        {campaign.name}
                                                    </a>
                                                </TableCell>
                                                <TableCell>
                                                    {campaign.cycleName || (campaign.cycleId ? `Cycle ${campaign.cycleId}` : "—")}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={campaign.status === "active" ? "default" : "secondary"}>
                                                        {campaign.status || "Draft"}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {campaign.retailerName || "—"}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                                    <FileText className="h-10 w-10 mb-4 opacity-20" />
                                    <p>No campaigns under this booking yet.</p>
                                    <p className="text-sm mt-1">
                                        Go to <a className="underline cursor-pointer" onClick={() => setLocation('/campaigns')}>Campaigns</a> to create one.
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
